import { ReactNode } from 'react';
import {
  ToggleButtonGroup,
  ToggleButton,
  type Key,
} from 'react-aria-components';
import { cx } from './cx';
import { typography } from './Text';

export interface FilterChipOption {
  value: string;
  label: string;
  /** Optional trailing count (e.g. Owned (3)). */
  count?: number;
  icon?: ReactNode;
}

export interface FilterChipsProps {
  options: FilterChipOption[];
  value: string;
  onChange: (value: string) => void;
  'aria-label'?: string;
  className?: string;
}

/**
 * Single-select filter pills — the catalog's All / Owned / Starred toggles.
 * Built on react-aria `ToggleButtonGroup`; the selected pill tints brand.
 * (For a tab-style segmented control use Tabs; binary on/off use Checkbox.)
 */
export const FilterChips = ({
  options,
  value,
  onChange,
  className,
  ...rest
}: FilterChipsProps) => (
  <ToggleButtonGroup
    selectionMode="single"
    selectedKeys={value ? [value] : []}
    onSelectionChange={keys => {
      const next = [...keys][0] as Key | undefined;
      if (next !== undefined) onChange(String(next)); // ignore deselect — single-required
    }}
    aria-label={rest['aria-label']}
    className={cx('flex flex-wrap items-center gap-2', className)}
  >
    {options.map(option => (
      <ToggleButton
        key={option.value}
        id={option.value}
        className={cx(
          typography.label,
          'inline-flex h-9 cursor-pointer appearance-none items-center gap-1.5 rounded-full border bg-transparent px-3.5 outline-none transition',
          'border-strong bg-surface text-fg-secondary shadow-xs hover:bg-muted-strong',
          'focus-visible:ring-4 focus-visible:ring-[rgb(var(--c-ring)/0.24)]',
          'selected:border-brand-solid/50 selected:bg-brand-solid/12 selected:text-brand-text selected:shadow-none',
        )}
      >
        {option.icon}
        {option.label}
        {typeof option.count === 'number' && (
          <span
            className={cx(
              typography.caption,
              typography.mono,
              'font-semibold text-fg-tertiary group-selected:text-brand-text',
            )}
          >
            {option.count}
          </span>
        )}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);
